import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { 
  DENIAL_CATEGORY_LABELS, 
  DENIAL_CATEGORY_DESCRIPTIONS,
  DENIAL_CATEGORY_COLORS,
  CONFIDENCE_LABELS,
  type DenialCategory,
  type DenialCategoryConfidence
} from '@/lib/normalization/denialNormalization';
import { AlertCircle, CheckCircle, Cpu } from 'lucide-react';

interface DenialCategoryBadgeProps {
  category: DenialCategory;
  confidence?: DenialCategoryConfidence;
  rawText?: string | null;
  showTooltip?: boolean;
  className?: string;
}

const CONFIDENCE_ICONS: Record<DenialCategoryConfidence, React.ComponentType<{ className?: string }>> = {
  TEMPLATE: AlertCircle,
  VERIFIED: CheckCircle,
  LEARNED: Cpu,
};

export function DenialCategoryBadge({ 
  category, 
  confidence = 'TEMPLATE',
  rawText,
  showTooltip = true,
  className 
}: DenialCategoryBadgeProps) {
  const ConfidenceIcon = CONFIDENCE_ICONS[confidence];
  const colorClass = DENIAL_CATEGORY_COLORS[category];

  const badge = (
    <Badge className={cn("gap-1", colorClass, className)}>
      {DENIAL_CATEGORY_LABELS[category]}
      <ConfidenceIcon className="h-3 w-3 ml-0.5 opacity-70" />
    </Badge>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-2">
            <p className="font-medium">{DENIAL_CATEGORY_LABELS[category]}</p>
            <p className="text-xs text-muted-foreground">
              {DENIAL_CATEGORY_DESCRIPTIONS[category]}
            </p>
            <div className="flex items-center gap-1 text-xs">
              <ConfidenceIcon className="h-3 w-3" />
              <span>{CONFIDENCE_LABELS[confidence]}</span>
            </div>
            {rawText && (
              <div className="border-t pt-2 mt-2">
                <p className="text-xs font-medium">Original denial text:</p>
                <p className="text-xs text-muted-foreground italic">{rawText}</p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
